import {Stack} from '@mantine/core';
import {Field, Formik, FormikHelpers} from 'formik';
import {useNavigate} from 'react-router-dom';
import {FormikTextarea, FormikTextInput, FormWithButtons, PlatformDrawer, PlatformFieldset} from '@flexinet/ui-components';
import {useStartExecutionMutation} from '../hooks/useExecutionsQuery.ts';
import {ExecutePlaybookInput} from '../types.ts';

interface StartExecutionDrawerProps {
    playbookId: string;
    playbookName?: string;
    opened: boolean;
    onClose: () => void;
}

type FormValues = {
    name: string;
    description: string;
    executionInput: string;
};

const validateForm = (values: FormValues) => {
    const errors: Partial<Record<keyof FormValues, string>> = {};
    if (!values.name.trim()) {
        errors.name = 'Name is required';
    }
    if (!values.executionInput.trim()) {
        errors.executionInput = 'Execution input is required';
    } else {
        try {
            JSON.parse(values.executionInput);
        } catch {
            errors.executionInput = 'Execution input must be valid JSON';
        }
    }
    return errors;
};

export const StartExecutionDrawer = ({
                                         playbookId,
                                         playbookName,
                                         opened,
                                         onClose,
                                     }: StartExecutionDrawerProps) => {

    const navigate = useNavigate();
    const startMutation = useStartExecutionMutation();

    const defaultName = playbookName
        ? `${playbookName} run ${new Date().toISOString()}`
        : `run ${new Date().toISOString()}`;

    const initialValues: FormValues = {
        name: defaultName,
        description: '',
        executionInput: '{}',
    };

    const handleSubmit = (values: FormValues, helpers: FormikHelpers<FormValues>) => {
        const input: ExecutePlaybookInput = {
            playbookId,
            name: values.name.trim(),
            executionInput: values.executionInput,
            description: values.description.trim() || undefined,
        };
        startMutation.mutate(input, {
            onSuccess: (execution) => {
                helpers.setSubmitting(false);
                onClose();
                if (execution?.id) {
                    navigate(`/executions/${execution.id}`);
                }
            },
            onError: () => {
                helpers.setSubmitting(false);
            },
        });
    };

    return (
        <PlatformDrawer
            opened={opened}
            onClose={onClose}
            title={'Start Execution'}
            size={'xl'}
        >
            <Formik
                initialValues={initialValues}
                validate={validateForm}
                onSubmit={handleSubmit}
            >
                {({handleSubmit: submit, isSubmitting, ...rest}) => (
                    <FormWithButtons
                        handleSubmit={submit}
                        isSubmitting={isSubmitting || startMutation.isPending}
                        handleCancel={onClose}
                        debug={false}
                        {...rest}
                    >
                        <Stack gap={'xl'}>
                            <PlatformFieldset legend={'Run Details'}>
                                <Field
                                    name={'name'}
                                    label={'Name'}
                                    description={'Identifier for this execution'}
                                    component={FormikTextInput}
                                    withAsterisk
                                    my={'xl'}
                                />
                                <Field
                                    name={'description'}
                                    label={'Description'}
                                    description={'Optional notes about this run'}
                                    component={FormikTextarea}
                                    my={'xl'}
                                />
                            </PlatformFieldset>

                            <PlatformFieldset legend={'Execution Input'}>
                                <Field
                                    name={'executionInput'}
                                    label={'Input JSON'}
                                    description={'JSON payload passed to the playbook (use {} for none)'}
                                    component={FormikTextarea}
                                    autosize
                                    minRows={6}
                                    withAsterisk
                                    my={'xl'}
                                    styles={{input: {fontFamily: 'monospace'}}}
                                />
                            </PlatformFieldset>
                        </Stack>
                    </FormWithButtons>
                )}
            </Formik>
        </PlatformDrawer>
    );
};
